import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { getAppLanguage, setLanguagePersistMode, toUiLocale, UiLang } from '@/i18n/config';

const PUBLIC_KEY = 'public_lang';

export const readQueryLang = (): UiLang | null => {
  try {
    return toUiLocale(new URLSearchParams(window.location.search).get('lang'));
  } catch {
    return null;
  }
};

// Offentlig side: ?lang= → kundens preferred_language (fra backend) → eget valg på offentlig side → norsk.
// Bytter aldri den innloggede brukerens lagrede språk (persist-modus 'public').
export function usePublicLanguage(serverLang?: string | null) {
  const { i18n } = useTranslation();
  const chosen = useRef<UiLang | null>(null);

  useEffect(() => {
    setLanguagePersistMode('public');
    return () => {
      setLanguagePersistMode('app');
      const appLang = getAppLanguage();
      if (i18n.language !== appLang) i18n.changeLanguage(appLang);
    };
  }, [i18n]);

  useEffect(() => {
    let stored: UiLang | null = null;
    try { stored = toUiLocale(localStorage.getItem(PUBLIC_KEY)); } catch { /* ignore */ }
    const target: UiLang = chosen.current || readQueryLang() || toUiLocale(serverLang) || stored || 'no';
    if (i18n.language !== target) i18n.changeLanguage(target);
  }, [serverLang, i18n]);

  const setPublicLanguage = (lang: UiLang) => {
    chosen.current = lang;
    try { localStorage.setItem(PUBLIC_KEY, lang); } catch { /* ignore */ }
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('lang', lang);
      window.history.replaceState({}, '', url.toString());
    } catch { /* ignore */ }
    i18n.changeLanguage(lang);
  };

  return { lang: (toUiLocale(i18n.language) || 'no') as UiLang, setPublicLanguage };
}
